// ─── Shared Gear Exchange import trigger button ─────────────────────────────
// Full-width block button with status text, used by both search and social formats.

import { Loader2, ExternalLink } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

interface GearExchangeImportButtonProps {
  /** GX total asset count (from gxData.total_assets). Null/undefined if data not yet loaded. */
  totalAssets?: number | null;
  /** Whether GX search is currently loading */
  loading?: boolean;
  /** Whether GX search errored */
  error?: boolean;
  /** FordShare IDs associated with the campaign */
  fordshareIds?: string[];
  /** Called when the user clicks to open the GX modal */
  onClick: () => void;
}

export function GearExchangeImportButton({
  totalAssets,
  loading = false,
  error = false,
  fordshareIds,
  onClick,
}: GearExchangeImportButtonProps) {
  const hasIds = !!(fordshareIds && fordshareIds.length > 0);
  const hasAssets = totalAssets != null && totalAssets > 0;
  const isClickable = hasIds && hasAssets && !loading && !error;

  const idSuffix =
    fordshareIds && fordshareIds.length > 1
      ? ` across ${fordshareIds.length} FordShare IDs`
      : fordshareIds && fordshareIds.length === 1
        ? ` for FordShare ID ${fordshareIds[0]}`
        : '';

  const primaryText = !hasIds
    ? 'Set a FordShare ID to import assets from Gear Exchange'
    : loading
      ? 'Searching Gear Exchange for assets…'
      : error
        ? 'Could not connect to Gear Exchange'
        : hasAssets
          ? `${totalAssets} asset${totalAssets !== 1 ? 's' : ''} found on Gear Exchange${idSuffix}`
          : `No assets found on Gear Exchange${idSuffix}`;

  const secondaryText = !hasIds
    ? 'Add one in the Content Tracker table'
    : loading
      ? `Searching ${fordshareIds!.length} FordShare ID${fordshareIds!.length !== 1 ? 's' : ''}…`
      : error
        ? 'Try refreshing the page'
        : hasAssets
          ? 'Click to preview and import'
          : 'Note: Assets linked to FordShare IDs created before 26/03/2026 are not accessible via the API. Please upload these assets manually.';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => isClickable && onClick()}
          disabled={!isClickable}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-150 text-left ${
            isClickable
              ? 'bg-slate-50 hover:bg-slate-100 cursor-pointer group'
              : 'bg-slate-50 cursor-default'
          }`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isClickable ? 'bg-slate-200 text-slate-600' : 'bg-slate-100 text-slate-400'
            }`}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ExternalLink className="w-4 h-4" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-600">{primaryText}</p>
            <p className="text-[10px] text-slate-400 mt-0.5 group-hover:text-slate-500 transition-colors">
              {secondaryText}
            </p>
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">
        Assets uploaded to Ford&apos;s Gear Exchange can be previewed and imported directly — no
        need to download and re-upload them manually.
      </TooltipContent>
    </Tooltip>
  );
}
